import { PreofferMode, PreofferSettingsValues } from '@data/interfaces/preoffer.interface'

/**
 * Lo que pinta y valida la configuración de la preoferta (plan v4.3 §8.1, §9.4 y §2b). Los límites son los del DTO de
 * la API (`UpdatePreofferThresholdsDto`): si cambian allá, se cambian aquí. Funciones puras para probarlas sin plantilla.
 */

export const PREOFFER_LIMITS = {
  maxPreapprovedAmount: { min: 1, max: 100_000_000 },
  minTotalScore: { min: 0, max: 110 },
  validityDays: { min: 1, max: 90 },
  reason: 10,
} as const

export const MODE_UI: Readonly<Record<PreofferMode, { label: string; tone: 'neutral' | 'info' | 'ok'; text: string }>> = {
  off: { label: 'Apagada', tone: 'neutral', text: 'No evalúa a los clientes nuevos ni envía nada.' },
  shadow: { label: 'Sombra', tone: 'info', text: 'Decide y lo muestra en el panel como «habría salido»; no envía nada al cliente.' },
  live: { label: 'Encendida', tone: 'ok', text: 'Decide y envía la carta por correo a los clientes nuevos elegibles.' },
}

export const MODE_OPTIONS = (Object.keys(MODE_UI) as PreofferMode[]).map((value) => ({ value, label: MODE_UI[value].label }))

export const PERSON_TYPE_OPTIONS: ReadonlyArray<{ value: 'PM' | 'PF'; label: string }> = [
  { value: 'PM', label: 'Persona moral' },
  { value: 'PF', label: 'Persona física' },
]

export type ThresholdsForm = Omit<PreofferSettingsValues, 'mode'>

export function thresholdsOf(values: PreofferSettingsValues): ThresholdsForm {
  return {
    maxPreapprovedAmount: values.maxPreapprovedAmount,
    minTotalScore: values.minTotalScore,
    personTypes: [...values.personTypes],
    validityDays: values.validityDays,
  }
}

/** Si el formulario difiere de lo guardado. Los tipos de persona se comparan sin importar el orden. */
export function thresholdsChanged(form: ThresholdsForm, saved: PreofferSettingsValues): boolean {
  const sameList = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join()
  return (
    Number(form.maxPreapprovedAmount) !== saved.maxPreapprovedAmount ||
    Number(form.minTotalScore) !== saved.minTotalScore ||
    Number(form.validityDays) !== saved.validityDays ||
    !sameList(form.personTypes, saved.personTypes)
  )
}

/** Los mismos mensajes que la API, para no mandar algo que va a regresar con 400. */
export function thresholdErrors(form: ThresholdsForm, reason: string): string[] {
  const errors: string[] = []
  const inRange = (value: unknown, { min, max }: { min: number; max: number }) => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max
  if (!inRange(form.maxPreapprovedAmount, PREOFFER_LIMITS.maxPreapprovedAmount)) errors.push('El tope va de $1 a $100,000,000')
  if (!inRange(form.minTotalScore, PREOFFER_LIMITS.minTotalScore)) errors.push('El puntaje mínimo va de 0 a 110')
  if (!inRange(form.validityDays, PREOFFER_LIMITS.validityDays) || !Number.isInteger(form.validityDays)) errors.push('La vigencia va de 1 a 90 días, en días enteros')
  if (!form.personTypes.length) errors.push('Elige al menos un tipo de persona')
  if (reason.trim().length < PREOFFER_LIMITS.reason) errors.push(`Escribe el motivo con al menos ${PREOFFER_LIMITS.reason} caracteres`)
  return errors
}

/** Una versión del historial en una línea: tope, puntaje, tipos de persona y vigencia. */
export function versionSummary(values: Omit<PreofferSettingsValues, 'mode'>): string {
  const money = `$${Math.round(values.maxPreapprovedAmount).toLocaleString('es-MX')}`
  return `Tope ${money} · puntaje ${values.minTotalScore} · ${values.personTypes.join('/')} · ${values.validityDays} días`
}
